<template>
  <div class="module-rules__caption">
    Map to:
  </div>
  <Block>
    <TransactionFormType
      v-if="instance"
      data-refid="transactionTypeMapping"
      :label="TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.TYPE]"
      :instance
      :size
      :restrictions="['cash dividend', 'cash interest', 'realized gain or loss']"
      :disabled
      @ready="(e: any) => !type && e.focus()"
    />
  </Block>
  <template v-if="type">
    <Block>
      <EntryDate
        v-model="date"
        :json-data
        :disabled
        :label="TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DATE]"
      />
    </Block>
    <MappingRulesActionsEntry
      v-for="(entrySettings, index) in settings"
      :key="entrySettings.name"
      :model-value="actionsModel?.[0]"
      :settings="entrySettings"
      :json-data
      :index
      :disabled
      @update:model-value="$event && (actionsModel = [$event])"
    />
    <Block>
      <EntryField
        v-model="description"
        data-refid="mappingRuleDescription"
        :json-data
        :disabled
        :label="TRANSACTION_FIELD_LABEL[TRANSACTION_FIELD.DESCRIPTION]"
      />
    </Block>
  </template>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeMount, ref, watch } from 'vue'

import { InputSize, TransactionClass } from '@types'
import { MappingRuleAction } from './utils/types'

import { TRANSACTION_FIELD, TRANSACTION_FIELD_LABEL } from '@/const'

import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import TransactionFormType from '@/entities/transactions/components/TransactionFormType.vue'
import MappingRulesActionsEntry from './MappingRulesActionsEntry.vue'
import EntryField from './components/EntryField.vue'
import EntryDate from './components/EntryDate.vue'
import Block from './components/Block.vue'

type Props = {
  jsonData?: Record<string, unknown>
  disabled?: boolean
}

defineProps<Props>()

const actionsModel = defineModel<MappingRuleAction[]>({
  default: []
})

const transactionsSettingsStore = useTransactionsSettingsStore()

const size = inject<InputSize>('size')

const instance = ref<TransactionClass>()

const type = computed(
  () => instance.value?.field<string>(TRANSACTION_FIELD.TYPE).value,
)

const settings = computed(() => instance.value?.settings?.entries)

const date = computed({
  get() {
    return actionsModel.value?.[0]?.date
  },
  set(date) {
    actionsModel.value = [{
      ...actionsModel.value?.[0],
      date,
    }]
  },
})

const description = computed({
  get() {
    return actionsModel.value?.[0]?.description
  },
  set(description) {
    actionsModel.value = [{
      ...actionsModel.value?.[0],
      description,
    }]
  },
})

watch(type, (value, prev) => {
  if (!prev && actionsModel.value.length === 1) return
  actionsModel.value = [{
    transaction_type: value,
    type: 'map to transaction',
    date: {
      isMappedFromJson: true,
    },
    entries: Array.from({ length: settings.value?.length || 0 }, (_, index) => {
      let account = undefined
      let amount = undefined
      let asset = undefined

      const accountSettings = settings.value?.[index]?.account
      const amountSettings = settings.value?.[index]?.amount
      const assetSettings = settings.value?.[index]?.asset

      if (accountSettings) {
        if (accountSettings.default || accountSettings.value) {
          account = {
            isMappedFromJson: false,
            value: accountSettings.default || accountSettings.value,
          }
        } else {
          account = { useContextAccount: !accountSettings.optional }
        }
      }

      if (amountSettings) {
        amount = { isMappedFromJson: true }
      }

      if (assetSettings) {
        asset = { isMappedFromJson: true }
      }

      return {
        account,
        amount,
        asset,
      }
    }),
    description: {
      isMappedFromJson: true,
    },
  }]
})

onBeforeMount(() => {
  instance.value = new TransactionClass({
    type: actionsModel.value?.[0]?.transaction_type,
  }, true)
  if (transactionsSettingsStore.initFlag) return
  transactionsSettingsStore.fetch()
})
</script>

<style>
.mapping-rules-action {
  &__caption {
    @apply py-2 my-2 -mx-4 px-4;
    @apply bg-body;
    @apply font-medium text-sm sm:text-xs;
    @apply uppercase;
  }
}
</style>
